import React, { useEffect, useState } from "react";

const CONSENT_COOKIE = "cookie_consent_v1";
const CONSENT_MAX_AGE_DAYS = 180;

// ---------- Cookie Helpers ----------
function setCookie(name, value, days) {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" +
        name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") +
        "=([^;]*)"
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
}

// ---------- GA Loader ----------
function loadGA(measurementId) {
  if (!measurementId) return;

  // Prevent double-loading
  if (window.__gaLoaded) return;
  window.__gaLoaded = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId
  )}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;

  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    anonymize_ip: true,
  });
}

// ---------- Component ----------
export default function CookieBanner({ gaMeasurementId, reset}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!reset) return;
    setCookie(CONSENT_COOKIE, "", -1);
    setVisible(true);
  }, [reset]);

  useEffect(() => {
    const consent = getCookie(CONSENT_COOKIE);

    if (!consent || consent === "rejected") {
      setVisible(true);
    }

    if (consent === "accepted") {
      loadGA(gaMeasurementId);
    }
  }, [gaMeasurementId]);

  const applyConsent = (value) => {
    setCookie(CONSENT_COOKIE, value, CONSENT_MAX_AGE_DAYS);
    setVisible(false);

    if (value === "accepted") {
      loadGA(gaMeasurementId);
    }
  };

  // Optional: allow footer link to reopen banner
  useEffect(() => {
    window.openCookieSettings = () => setVisible(true);
  }, []);

  if (!visible) return null;

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.textBlock}>
          <div style={styles.title}>Cookies</div>
          <div style={styles.description}>
            We use cookies for basic site functionality and optional analytics
            to understand usage.{" "}
            <a href="/privacy-policy" style={styles.link}>
              Privacy Policy
            </a>
            .
          </div>
        </div>

        <div style={styles.buttons}>
          <button
            style={styles.reject}
            onClick={() => applyConsent("rejected")}
          >
            Reject
          </button>

          <button
            style={styles.accept}
            onClick={() => applyConsent("accepted")}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Styles ----------
const styles = {
  container: {
    position: "fixed",
    left: 16,
    right: 16,
    bottom: 16,
    maxWidth: 920,
    margin: "0 auto",
    background: "#111",
    color: "#fff",
    padding: "14px 16px",
    borderRadius: 12,
    boxShadow: "0 12px 30px rgba(0,0,0,.25)",
    zIndex: 9999,
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial",
  },
  content: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  textBlock: {
    minWidth: 240,
    flex: 1,
  },
  title: {
    fontWeight: 700,
    marginBottom: 4,
  },
  description: {
    fontSize: 14,
    lineHeight: 1.4,
    opacity: 0.92,
  },
  link: {
    color: "#9ad",
    textDecoration: "underline",
  },
  buttons: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  reject: {
    background: "transparent",
    color: "#fff",
    border: "1px solid rgba(255,255,255,.35)",
    padding: "10px 12px",
    borderRadius: 10,
    cursor: "pointer",
  },
  accept: {
    background: "#fff",
    color: "#111",
    border: "none",
    padding: "10px 12px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
};